/**
 * ColumnsBlock — Custom TipTap node for column layouts (1, 2, or 3 cols)
 * with visual Card children that are editable inline.
 */
import { Node, mergeAttributes } from '@tiptap/react';
import { NodeViewWrapper, NodeViewContent, ReactNodeViewRenderer } from '@tiptap/react';
import { useState, useCallback } from 'react';
import { Columns, Trash2, GripVertical, MoreVertical, Settings2 } from 'lucide-react';
import { icons as lucideIcons } from 'lucide-react';
import { useEditorTheme } from '../EditorThemeContext';
import IconPicker from '../components/IconPicker';

// ============= Columns Block NodeView =============
const ColumnsBlockView = ({ node, updateAttributes, deleteNode, editor, getPos }) => {
  const [showSettings, setShowSettings] = useState(false);
  const themeId = useEditorTheme();
  const isLight = themeId === 'light';
  const cols = node.attrs.cols || 2;

  const setCols = useCallback((newCols) => {
    const currentChildren = node.content.content.length;
    updateAttributes({ cols: newCols });

    // Add cards if needed
    if (newCols > currentChildren) {
      const pos = getPos();
      const endPos = pos + node.nodeSize - 1;
      for (let i = currentChildren; i < newCols; i++) {
        editor.chain().insertContentAt(endPos, {
          type: 'columnCard',
          attrs: { title: `Card ${i + 1}`, description: 'Description', icon: 'zap' },
        }).run();
      }
    }
    setShowSettings(false);
  }, [node, updateAttributes, editor, getPos]);

  return (
    <NodeViewWrapper className="columns-block-wrapper my-6 relative group" data-testid="columns-block" data-cols={cols}>
      {/* Controls Bar */}
      <div className="columns-block-controls absolute -top-8 right-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <div className="relative">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              isLight ? 'bg-white border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50' : 'bg-slate-800 border-white/10 text-slate-300 hover:text-white hover:bg-slate-700'
            }`}
            data-testid="columns-settings-btn"
          >
            <Settings2 className="w-3.5 h-3.5" /> Edit Columns
          </button>
          {showSettings && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowSettings(false)} />
              <div className={`absolute z-50 top-full mt-1 right-0 border rounded-xl shadow-2xl p-4 w-56 ${isLight ? 'bg-white border-gray-200' : 'bg-[#1e1e1e] border-white/10'}`} data-testid="columns-settings-popup">
                <div className="flex items-center gap-2 mb-3">
                  <Columns className={`w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${isLight ? 'text-gray-900' : 'text-white'}`}>Edit Columns Attributes</span>
                </div>
                <div className="mb-3">
                  <label className={`flex items-center gap-2 text-xs mb-1.5 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
                    <Columns className="w-3.5 h-3.5" /> Cols
                  </label>
                  <select
                    value={cols}
                    onChange={(e) => setCols(parseInt(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:border-[#00A1B2] focus:outline-none ${isLight ? 'bg-gray-50 border-gray-200 text-gray-900' : 'bg-slate-800 border-slate-700 text-white'}`}
                    data-testid="columns-count-select"
                  >
                    <option value={1}>One</option>
                    <option value={2}>Two</option>
                    <option value={3}>Three</option>
                  </select>
                </div>
                <div className={`flex items-center justify-between pt-2 border-t ${isLight ? 'border-gray-200' : 'border-white/10'}`}>
                  <button
                    onClick={() => { setShowSettings(false); deleteNode(); }}
                    className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded transition-colors"
                    data-testid="columns-delete-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowSettings(false)}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${isLight ? 'bg-gray-100 hover:bg-gray-200 text-gray-900' : 'bg-slate-700 hover:bg-slate-600 text-white'}`}
                    data-testid="columns-save-btn"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Columns Grid — use inline styles for reliable grid layout */}
      <NodeViewContent
        as="div"
        style={{
          display: 'grid',
          gap: '1rem',
          gridTemplateColumns: cols === 1 ? '1fr' : cols === 2 ? '1fr 1fr' : '1fr 1fr 1fr',
        }}
        data-testid="columns-grid"
      />
    </NodeViewWrapper>
  );
};

// ============= Column Card NodeView =============
const ColumnCardView = ({ node, updateAttributes, deleteNode }) => {
  const [showSettings, setShowSettings] = useState(false);
  const themeId = useEditorTheme();
  const isLight = themeId === 'light';
  const { title, description, icon, url, imagePath, cta, horizontal, cardType, iframeSrc } = node.attrs;
  const [editForm, setEditForm] = useState({ title, description, icon, url, imagePath, cta, horizontal, iframeSrc });

  const saveEdits = useCallback(() => {
    updateAttributes(editForm);
    setShowSettings(false);
  }, [editForm, updateAttributes]);

  const openSettings = useCallback(() => {
    setEditForm({ title: node.attrs.title, description: node.attrs.description, icon: node.attrs.icon, url: node.attrs.url, imagePath: node.attrs.imagePath, cta: node.attrs.cta, horizontal: node.attrs.horizontal, iframeSrc: node.attrs.iframeSrc });
    setShowSettings(true);
  }, [node.attrs]);

  // Iframe card rendering
  if (cardType === 'iframe') {
    return (
      <NodeViewWrapper className="column-card-wrapper" data-testid="column-card-iframe">
        <div className="relative group/card">
          <div
            className={`rounded-xl border overflow-hidden transition-all ${
              isLight ? 'border-gray-200 bg-gray-50 hover:border-gray-300' : 'border-white/10 bg-[#1a1a1a] hover:border-white/20'
            }`}
          >
            {iframeSrc ? (
              <iframe
                src={iframeSrc}
                title={title || 'Embedded content'}
                className="w-full aspect-video rounded-xl"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="card-iframe-embed"
              />
            ) : (
              <div className={`flex items-center justify-center aspect-video ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
                <span className="text-sm">No iframe source</span>
              </div>
            )}
          </div>
          {/* Edit button */}
          <button
            className={`absolute top-2 right-2 p-1.5 rounded-lg opacity-0 group-hover/card:opacity-100 transition-all z-10 ${
              isLight ? 'text-gray-600 bg-white/90 hover:bg-white shadow-sm' : 'text-white bg-black/60 hover:bg-black/80'
            }`}
            onClick={(e) => { e.stopPropagation(); openSettings(); }}
            data-testid="iframe-edit-btn"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {/* Edit iframe popup */}
          {showSettings && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowSettings(false)} />
              <div className={`absolute z-50 top-0 right-0 translate-x-[calc(100%+8px)] border rounded-xl shadow-2xl p-4 w-64 ${
                isLight ? 'bg-white border-gray-200' : 'bg-[#1e1e1e] border-white/10'
              }`} data-testid="iframe-settings-popup">
                <div className="flex items-center gap-2 mb-4">
                  <Settings2 className={`w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${isLight ? 'text-gray-900' : 'text-white'}`}>Edit Embed</span>
                </div>
                <div className="space-y-2.5 mb-4">
                  <div>
                    <label className={`flex items-center gap-1.5 text-[11px] mb-1 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Embed URL</label>
                    <input
                      value={editForm.iframeSrc || ''}
                      onChange={(e) => setEditForm(f => ({ ...f, iframeSrc: e.target.value }))}
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-xs focus:border-[#00A1B2] focus:outline-none ${
                        isLight ? 'bg-gray-50 border-gray-200 text-gray-900' : 'bg-slate-800 border-slate-700 text-white'
                      }`}
                      placeholder="https://www.youtube.com/embed/..."
                      data-testid="iframe-edit-src"
                    />
                  </div>
                  <div>
                    <label className={`flex items-center gap-1.5 text-[11px] mb-1 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Title</label>
                    <input
                      value={editForm.title || ''}
                      onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))}
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-xs focus:border-[#00A1B2] focus:outline-none ${
                        isLight ? 'bg-gray-50 border-gray-200 text-gray-900' : 'bg-slate-800 border-slate-700 text-white'
                      }`}
                      placeholder="Video title"
                      data-testid="iframe-edit-title"
                    />
                  </div>
                </div>
                <div className={`flex items-center justify-between pt-2 border-t ${isLight ? 'border-gray-200' : 'border-white/10'}`}>
                  <button onClick={() => { setShowSettings(false); deleteNode(); }} className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded transition-colors" data-testid="iframe-delete-btn">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button onClick={saveEdits} className="px-3 py-1.5 bg-[#00A1B2] text-white text-xs rounded-lg hover:opacity-90 transition-opacity" data-testid="iframe-save-btn">
                    Save
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </NodeViewWrapper>
    );
  }

  // Get lucide icon component dynamically
  const IconComponent = getCardIcon(icon);

  return (
    <NodeViewWrapper className="column-card-wrapper" data-testid="column-card">
      <div className="relative group/card">
        {/* Card Visual */}
        <div
          className={`column-card-visual p-5 rounded-xl border transition-all cursor-pointer ${
            isLight
              ? 'border-gray-200 bg-gray-50 hover:border-gray-300'
              : 'border-white/10 bg-[#1a1a1a] hover:border-white/20'
          } ${horizontal ? 'flex items-start gap-4' : ''}`}
          onClick={openSettings}
          data-testid="card-visual"
        >
          {/* Three-dot menu */}
          <button
            className={`absolute top-2 right-2 p-1 rounded opacity-0 group-hover/card:opacity-100 transition-all z-10 ${
              isLight ? 'text-gray-400 hover:text-gray-700 hover:bg-gray-200' : 'text-slate-500 hover:text-white hover:bg-white/10'
            }`}
            onClick={(e) => { e.stopPropagation(); openSettings(); }}
            data-testid="card-menu-btn"
          >
            <MoreVertical className="w-4 h-4" />
          </button>

          {/* Icon */}
          {IconComponent && (
            <div className={`mb-3 ${horizontal ? 'mb-0 shrink-0' : ''}`}>
              <IconComponent className="w-6 h-6 text-[#00A1B2]" />
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className={`text-sm font-semibold mb-1 leading-tight ${isLight ? 'text-gray-900' : 'text-white'}`}>{title || 'Untitled'}</h4>
            <p className={`text-xs leading-relaxed ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>{description || 'Add a description...'}</p>
            {cta && (
              <span className="inline-block mt-2 text-xs text-[#00A1B2] font-medium">{cta}</span>
            )}
          </div>
        </div>

        {/* Edit Card Popup */}
        {showSettings && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowSettings(false)} />
            <div className={`absolute z-50 top-0 right-0 translate-x-[calc(100%+8px)] border rounded-xl shadow-2xl p-4 w-64 ${
              isLight ? 'bg-white border-gray-200' : 'bg-[#1e1e1e] border-white/10'
            }`} data-testid="card-settings-popup">
              <div className="flex items-center gap-2 mb-4">
                <Settings2 className={`w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
                <span className={`text-sm font-medium ${isLight ? 'text-gray-900' : 'text-white'}`}>Edit Card Attributes</span>
              </div>

              {/* Content Section */}
              <div className="mb-4">
                <h5 className={`text-xs font-semibold mb-2 ${isLight ? 'text-gray-700' : 'text-slate-300'}`}>Content</h5>

                <div className="space-y-2.5">
                  <div>
                    <label className={`flex items-center gap-1.5 text-[11px] mb-1 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Title</label>
                    <input
                      value={editForm.title || ''}
                      onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))}
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-xs focus:border-[#00A1B2] focus:outline-none ${
                        isLight ? 'bg-gray-50 border-gray-200 text-gray-900' : 'bg-slate-800 border-slate-700 text-white'
                      }`}
                      placeholder="Card title"
                      data-testid="card-edit-title"
                    />
                  </div>
                  <div>
                    <label className={`flex items-center gap-1.5 text-[11px] mb-1 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Description</label>
                    <textarea
                      value={editForm.description || ''}
                      onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-xs focus:border-[#00A1B2] focus:outline-none resize-none ${
                        isLight ? 'bg-gray-50 border-gray-200 text-gray-900' : 'bg-slate-800 border-slate-700 text-white'
                      }`}
                      rows={2}
                      placeholder="Card description"
                      data-testid="card-edit-description"
                    />
                  </div>
                  <div>
                    <label className={`flex items-center gap-1.5 text-[11px] mb-1 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Icon</label>
                    <IconPicker
                      value={editForm.icon || ''}
                      onChange={(val) => setEditForm(f => ({ ...f, icon: val }))}
                      isLight={isLight}
                    />
                  </div>
                  <div>
                    <label className={`flex items-center gap-1.5 text-[11px] mb-1 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>URL</label>
                    <input
                      value={editForm.url || ''}
                      onChange={(e) => setEditForm(f => ({ ...f, url: e.target.value }))}
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-xs focus:border-[#00A1B2] focus:outline-none ${
                        isLight ? 'bg-gray-50 border-gray-200 text-gray-900' : 'bg-slate-800 border-slate-700 text-white'
                      }`}
                      placeholder="/features/mcp"
                      data-testid="card-edit-url"
                    />
                  </div>
                  <div>
                    <label className={`flex items-center gap-1.5 text-[11px] mb-1 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Image Path</label>
                    <input
                      value={editForm.imagePath || ''}
                      onChange={(e) => setEditForm(f => ({ ...f, imagePath: e.target.value }))}
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-xs focus:border-[#00A1B2] focus:outline-none ${
                        isLight ? 'bg-gray-50 border-gray-200 text-gray-900' : 'bg-slate-800 border-slate-700 text-white'
                      }`}
                      placeholder="Enter Image Path"
                      data-testid="card-edit-image"
                    />
                  </div>
                  <div>
                    <label className={`flex items-center gap-1.5 text-[11px] mb-1 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Call To Action</label>
                    <input
                      value={editForm.cta || ''}
                      onChange={(e) => setEditForm(f => ({ ...f, cta: e.target.value }))}
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-xs focus:border-[#00A1B2] focus:outline-none ${
                        isLight ? 'bg-gray-50 border-gray-200 text-gray-900' : 'bg-slate-800 border-slate-700 text-white'
                      }`}
                      placeholder="Enter Call To Action"
                      data-testid="card-edit-cta"
                    />
                  </div>
                </div>
              </div>

              {/* Appearance */}
              <div className="mb-4">
                <h5 className={`text-xs font-semibold mb-2 ${isLight ? 'text-gray-700' : 'text-slate-300'}`}>Appearance</h5>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className={`text-xs ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Horizontal</span>
                  <button
                    type="button"
                    onClick={() => setEditForm(f => ({ ...f, horizontal: !f.horizontal }))}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                      editForm.horizontal ? 'bg-[#00A1B2]' : 'bg-slate-700'
                    }`}
                    data-testid="card-edit-horizontal"
                  >
                    <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition ${editForm.horizontal ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </label>
              </div>

              {/* Actions */}
              <div className={`flex items-center justify-between pt-3 border-t ${isLight ? 'border-gray-200' : 'border-white/10'}`}>
                <button
                  onClick={() => { setShowSettings(false); deleteNode(); }}
                  className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded transition-colors"
                  data-testid="card-delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <button
                  onClick={saveEdits}
                  className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                    isLight ? 'bg-gray-100 hover:bg-gray-200 text-gray-900' : 'bg-slate-700 hover:bg-slate-600 text-white'
                  }`}
                  data-testid="card-save-btn"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

// Dynamic icon resolver — supports all 1600+ Lucide icons
function getCardIcon(iconName) {
  if (!iconName) return null;
  // Convert kebab-case to PascalCase
  const pascal = iconName.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('');
  return lucideIcons[pascal] || null;
}

// ============= TipTap Node: columnsBlock =============
export const ColumnsBlockNode = Node.create({
  name: 'columnsBlock',
  group: 'block',
  content: 'columnCard+',
  defining: true,
  isolating: true,

  addAttributes() {
    return {
      cols: { default: 2, parseHTML: el => parseInt(el.getAttribute('data-cols')) || 2 },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="columns-block"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'columns-block', 'data-cols': HTMLAttributes.cols }), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ColumnsBlockView);
  },

  addStorage() {
    return { markdown: { serialize: columnsBlockSerializer } };
  },
});

function columnsBlockSerializer(state, node) {
  const cols = node.attrs.cols || 2;
  const children = [];
  node.content.forEach(child => {
    if (child.type.name === 'columnCard') {
      const a = child.attrs;
      if (a.cardType === 'iframe') {
        // Serialize back as <iframe> tag
        if (a.iframeHtml) {
          children.push(a.iframeHtml);
        } else if (a.iframeSrc) {
          children.push(`<iframe src="${a.iframeSrc}" title="${a.title || 'Embedded content'}" frameborder="0" className="w-full aspect-video rounded-xl" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`);
        }
      } else {
        let cardAttrs = `title="${a.title || 'Card'}" icon="${a.icon || 'file-text'}"`;
        if (a.url) cardAttrs += ` url="${a.url}"`;
        if (a.imagePath) cardAttrs += ` imagePath="${a.imagePath}"`;
        if (a.cta) cardAttrs += ` cta="${a.cta}"`;
        if (a.horizontal) cardAttrs += ` horizontal="true"`;
        children.push(`<Card ${cardAttrs}>\n${a.description || 'Description'}\n</Card>`);
      }
    }
  });
  state.write(`<Columns cols={${cols}}>\n${children.join('\n')}\n</Columns>\n\n`);
  state.closeBlock(node);
}

// ============= TipTap Node: columnCard =============
export const ColumnCardNode = Node.create({
  name: 'columnCard',
  group: 'block',
  content: '',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      title: {
        default: 'Card Title',
        parseHTML: el => el.getAttribute('data-title') || 'Card Title',
      },
      description: {
        default: 'Description',
        parseHTML: el => el.getAttribute('data-description') || 'Description',
      },
      icon: {
        default: 'file-text',
        parseHTML: el => el.getAttribute('data-icon') || 'file-text',
      },
      url: {
        default: '',
        parseHTML: el => el.getAttribute('data-url') || '',
      },
      imagePath: {
        default: '',
        parseHTML: el => el.getAttribute('data-image-path') || '',
      },
      cta: {
        default: '',
        parseHTML: el => el.getAttribute('data-cta') || '',
      },
      horizontal: {
        default: false,
        parseHTML: el => el.getAttribute('data-horizontal') === 'true',
      },
      cardType: {
        default: 'card',
        parseHTML: el => el.getAttribute('data-card-type') || 'card',
      },
      iframeSrc: {
        default: '',
        parseHTML: el => el.getAttribute('data-iframe-src') || '',
      },
      iframeHtml: {
        default: '',
        parseHTML: el => el.getAttribute('data-iframe-html') || '',
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="column-card"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, {
      'data-type': 'column-card',
      'data-title': HTMLAttributes.title,
      'data-description': HTMLAttributes.description,
      'data-icon': HTMLAttributes.icon,
      'data-url': HTMLAttributes.url,
      'data-image-path': HTMLAttributes.imagePath,
      'data-cta': HTMLAttributes.cta,
      'data-horizontal': HTMLAttributes.horizontal,
      'data-card-type': HTMLAttributes.cardType,
      'data-iframe-src': HTMLAttributes.iframeSrc,
      'data-iframe-html': HTMLAttributes.iframeHtml,
    })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ColumnCardView, {
      stopEvent: ({ event }) => {
        // Prevent ProseMirror from handling mouse events on this atom node
        // to avoid "Selection passed to setSelection must point at the current document"
        if (event.type === 'mousedown' || event.type === 'mouseup' || event.type === 'click') {
          return true;
        }
        return false;
      },
    });
  },

  addStorage() {
    return { markdown: { serialize() { /* handled by parent columnsBlock */ } } };
  },
});
